import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "./Reveal";
import { ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Calendar = () => {
  const { t, lang } = useLang();
  const c = t.calendar;
  const [events, setEvents] = useState([]);
  const [now, setNow] = useState(Date.now());

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/news`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const title = (e) => (lang === "en" && e.titolo_en ? e.titolo_en : e.titolo_it);

  const byDate = {};
  events.forEach((e) => {
    const k = (e.data || "").slice(0, 10);
    if (k) (byDate[k] = byDate[k] || []).push(e);
  });

  const upcoming = events
    .map((e) => ({ ...e, d: new Date((e.data || "").slice(0, 10)) }))
    .filter((e) => !isNaN(e.d) && e.d >= today)
    .sort((a, b) => a.d - b.d);
  const next = upcoming[0];

  let cd = null;
  if (next) {
    const diff = next.d.getTime() - now;
    cd = diff > 0
      ? { days: Math.floor(diff / 86400000), hours: Math.floor((diff % 86400000) / 3600000), mins: Math.floor((diff % 3600000) / 60000) }
      : { days: 0, hours: 0, mins: 0 };
  }

  const first = new Date(cursor.y, cursor.m, 1);
  const startWeekday = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthName = new Date(cursor.y, cursor.m, 1).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { month: "long", year: "numeric" });
  const key = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const isToday = (d) => today.getFullYear() === cursor.y && today.getMonth() === cursor.m && today.getDate() === d;
  const selectedEvents = selected ? byDate[selected] || [] : [];

  const move = (delta) => {
    setSelected(null);
    setCursor((cu) => {
      const nm = cu.m + delta;
      return { y: cu.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });
  };

  return (
    <section id="calendario" className="py-24 md:py-32 relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-2xl mb-14">
          <Reveal><span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-5">{c.tag}</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02]">{c.title}</h2>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Countdown card */}
          <div className="lg:col-span-4">
            <Reveal>
              <div data-testid="next-event-card" className="rounded-3xl bg-primary text-primary-foreground p-8 h-full flex flex-col justify-between min-h-[280px]">
                <div className="flex items-center gap-2 text-accent text-xs font-semibold uppercase tracking-widest">
                  <CalendarClock size={16} /> {c.next}
                </div>
                {next && cd ? (
                  <div>
                    <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight my-4 leading-tight">{title(next)}</h3>
                    <p className="text-primary-foreground/70 text-sm mb-6">
                      {next.d.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {[[cd.days, c.days], [cd.hours, c.hours], [cd.mins, c.mins]].map(([v, l], i) => (
                        <div key={i} className="bg-white/10 rounded-xl py-3 text-center">
                          <div className="font-display font-extrabold text-3xl">{v}</div>
                          <div className="text-[11px] uppercase tracking-wider text-primary-foreground/60">{l}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="font-serif-ed italic text-2xl text-primary-foreground/80 my-4">{c.none}</p>
                )}
              </div>
            </Reveal>
          </div>

          {/* Calendar */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-black/5 bg-surface p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-display font-bold text-xl capitalize">{monthName}</h3>
                  <div className="flex gap-2">
                    <button data-testid="cal-prev" onClick={() => move(-1)} className="grid place-items-center w-9 h-9 rounded-full border border-foreground/15 hover:bg-secondary transition-colors"><ChevronLeft size={16} /></button>
                    <button data-testid="cal-next" onClick={() => move(1)} className="grid place-items-center w-9 h-9 rounded-full border border-foreground/15 hover:bg-secondary transition-colors"><ChevronRight size={16} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1.5 mb-2">
                  {c.weekdays.map((w) => (
                    <div key={w} className="text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground py-1">{w}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const k = key(d);
                    const has = byDate[k] && byDate[k].length > 0;
                    const sel = selected === k;
                    return (
                      <button
                        key={i}
                        data-testid={has ? `cal-day-event-${d}` : `cal-day-${d}`}
                        onClick={() => has && setSelected(sel ? null : k)}
                        disabled={!has}
                        className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm relative transition-colors ${
                          sel ? "bg-primary text-primary-foreground" : has ? "bg-accent/15 text-foreground hover:bg-accent/25 cursor-pointer" : "text-foreground/70"
                        } ${isToday(d) && !sel ? "ring-1 ring-primary/40" : ""}`}
                      >
                        <span className={isToday(d) ? "font-bold" : ""}>{d}</span>
                        {has && <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${sel ? "bg-accent" : "bg-accent"}`} />}
                      </button>
                    );
                  })}
                </div>

                {selectedEvents.length > 0 && (
                  <div data-testid="cal-day-events" className="mt-6 pt-5 border-t border-foreground/10 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.eventsOn}</p>
                    {selectedEvents.map((e) => (
                      <div key={e.id} className="flex items-center gap-3">
                        <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full">{e.categoria}</span>
                        <span className="font-medium text-sm">{title(e)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
